import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// A PDF-export-safe map: Google's Static Maps image (html2canvas can't
// rasterize the live tiled map) styled to resemble the OpenStreetMap
// "Standard" look, with the screen markers drawn on top as real circles.
// Static Maps has no circle marker and caps URL length, so the markers are
// positioned by projecting lat/lon (Web Mercator) onto the image ourselves —
// perfectly round, constant pixel size, and no limit on screen count.

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';

// Palette taken from OSM Standard.
export const OSM_STYLE = [
  'feature:all|element:labels.icon|visibility:off',
  'feature:transit|visibility:off',
  'feature:poi|element:labels|visibility:off',
  'feature:poi|element:geometry|color:0xf2efe9',
  'feature:poi.park|element:geometry|color:0xcdebb0',
  'feature:poi.park|element:labels|visibility:on',
  'feature:landscape|element:geometry|color:0xf2efe9',
  'feature:landscape.man_made|element:geometry|color:0xe9e5dc',
  'feature:water|element:geometry|color:0xaad3df',
  'feature:water|element:labels.text.fill|color:0x5d90a0',
  'feature:road.local|element:geometry.fill|color:0xffffff',
  'feature:road.local|element:geometry.stroke|color:0xc9c4bb',
  'feature:road.arterial|element:geometry.fill|color:0xfcd6a4',
  'feature:road.arterial|element:geometry.stroke|color:0xc8a56a',
  'feature:road.highway|element:geometry.fill|color:0xf9b29c',
  'feature:road.highway|element:geometry.stroke|color:0xd9674a',
  'feature:road.highway.controlled_access|element:geometry.fill|color:0xe892a2',
  'feature:road.highway.controlled_access|element:geometry.stroke|color:0xdc2a67',
  'feature:administrative|element:geometry.stroke|color:0xb59cc4',
  'feature:all|element:labels.text.fill|color:0x333333',
  'feature:all|element:labels.text.stroke|color:0xffffff|weight:3',
];

export const MUTED_STYLE = [
  'feature:all|element:labels.icon|visibility:off',
  'feature:transit|visibility:off',
  'feature:poi|visibility:off',
  'feature:administrative.land_parcel|visibility:off',
  'feature:water|element:geometry|color:0xdfe9f1',
  'feature:landscape|element:geometry|color:0xf5f7fa',
  'feature:road|element:geometry|color:0xffffff',
  'feature:road|element:geometry.stroke|color:0xe3e8ee',
  'feature:road.highway|element:geometry|color:0xe8ecf0',
  'feature:all|element:labels.text.fill|color:0x94a3b8',
  'feature:all|element:labels.text.stroke|color:0xf5f7fa|weight:3',
];

export const DARK_STYLE = [
  'feature:all|element:labels.icon|visibility:off',
  'feature:transit|visibility:off',
  'feature:poi|visibility:off',
  'feature:all|element:geometry|color:0x14202b',
  'feature:water|element:geometry|color:0x0a1219',
  'feature:road|element:geometry|color:0x24323f',
  'feature:road.arterial|element:geometry|color:0x2d3d4c',
  'feature:all|element:labels.text.fill|color:0x6b7f90',
  'feature:all|element:labels.text.stroke|color:0x14202b|weight:3',
];

export interface DotSpec { d: number; style: React.CSSProperties; label?: React.ReactNode }
export const circleDot = (): DotSpec => ({ d: 14, style: { background: 'rgba(144,238,144,0.75)', border: '2px solid #1a8a1a' } });
export const brandDot = (): DotSpec => ({ d: 14, style: { background: '#0f6b44', border: '2px solid #fff', boxShadow: '0 0 0 3px rgba(15,107,68,0.28), 0 1px 3px rgba(0,0,0,0.35)' } });
export const solidDot = (): DotSpec => ({ d: 12, style: { background: '#4ADE80', border: '2.5px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.35)' } });
export const glowDot = (): DotSpec => ({ d: 11, style: { background: '#4ADE80', border: '2px solid #0D361F', boxShadow: '0 0 0 5px rgba(74,222,128,0.25), 0 0 14px rgba(74,222,128,0.8)' } });
export const bubbleDot = (n: number): DotSpec => ({
  d: n > 1 ? 20 + Math.min(n, 8) * 2 : 14,
  style: { background: 'rgba(74,222,128,0.85)', border: '2px solid #0a6e57', color: '#0B1220', fontWeight: 800, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  label: n > 1 ? n : undefined,
});

// Esri's Light Gray tiles are neutral grey; remap them onto the app's
// sage/mint palette (land #dbf0e4, water #a3bfae — derived from the brand's
// Google style JSON; streets stay near-white so they
// stand out, footprints/shorelines a touch darker). Done per pixel on a canvas — rather than a
// CSS filter — so it survives html2canvas in the PDF exports.
const LAND_STOPS: [number, [number, number, number]][] = [
  [196, [0xb2, 0xd2, 0xc0]],
  [214, [0xc6, 0xe3, 0xd4]],
  [232, [0xdb, 0xf0, 0xe4]],
  [255, [0xff, 0xff, 0xff]],
];
const WATER = [0xa3, 0xbf, 0xae];
function recolorPixels(d: Uint8ClampedArray) {
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i], g = d[i + 1], b = d[i + 2];
    // Esri water is #d0cfd4 — slightly blue-leaning grey; land greys are neutral.
    if (Math.abs(r - 0xd0) <= 3 && Math.abs(g - 0xcf) <= 3 && Math.abs(b - 0xd4) <= 3) {
      d[i] = WATER[0]; d[i + 1] = WATER[1]; d[i + 2] = WATER[2];
      continue;
    }
    const l = (r + g + b) / 3;
    let k = 0;
    while (k < LAND_STOPS.length - 2 && l > LAND_STOPS[k + 1][0]) k++;
    const [l0, c0] = LAND_STOPS[k], [l1, c1] = LAND_STOPS[k + 1];
    const t = Math.min(1, Math.max(0, (l - l0) / (l1 - l0)));
    d[i] = c0[0] + (c1[0] - c0[0]) * t;
    d[i + 1] = c0[1] + (c1[1] - c0[1]) * t;
    d[i + 2] = c0[2] + (c1[2] - c0[2]) * t;
  }
}

function RecoloredTile({ src, style }: { src: string; style: React.CSSProperties }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = ref.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      recolorPixels(data.data);
      ctx.putImageData(data, 0, 0);
    };
    img.src = src;
  }, [src]);
  return <canvas ref={ref} width={256} height={256} style={style} />;
}

const TILE = 256;
const project = (lat: number, lon: number, zoom: number) => {
  const scale = TILE * Math.pow(2, zoom);
  const sin = Math.sin((lat * Math.PI) / 180);
  return {
    x: ((lon + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
};
const unproject = (x: number, y: number, zoom: number) => {
  const scale = TILE * Math.pow(2, zoom);
  const n = Math.PI - (2 * Math.PI * y) / scale;
  return { lat: (180 / Math.PI) * Math.atan(Math.sinh(n)), lon: (x / scale) * 360 - 180 };
};

// Highest integer zoom at which every point fits inside the frame (minus
// padding), centred on the bounding box.
function fitView(pts: { lat: number; lon: number }[], w: number, h: number, pad: number, maxZoom: number) {
  const lats = pts.map(p => p.lat), lons = pts.map(p => p.lon);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLon = Math.min(...lons), maxLon = Math.max(...lons);
  let zoom = maxZoom;
  for (; zoom > 1; zoom--) {
    const a = project(maxLat, minLon, zoom), b = project(minLat, maxLon, zoom);
    if (b.x - a.x <= w - 2 * pad && b.y - a.y <= h - 2 * pad) break;
  }
  const a = project(maxLat, minLon, zoom), b = project(minLat, maxLon, zoom);
  const center = unproject((a.x + b.x) / 2, (a.y + b.y) / 2, zoom);
  return { zoom, center };
}

interface Props {
  screens: { lat: number; lon: number }[];
  // Image request size in CSS px (max 640 per side; fetched at 2x). Markers
  // are placed in percentages of this frame, so the rendered box should
  // match its aspect ratio.
  width: number;
  height: number;
  // Marker diameter in px.
  dot?: number;
  pad?: number;
  style?: React.CSSProperties;
  onFail?: () => void;
  mapStyle?: string[];
  // Per-marker look; receives how many screens share the location when
  // `group` is on (otherwise always 1).
  renderDot?: (units: number) => DotSpec;
  // Merge screens at the same coordinates into one marker.
  group?: boolean;
  // Draw the basemap from free, CORS-enabled XYZ tiles instead of Google's
  // Static Maps (which needs billing). Overrides `mapStyle`.
  tiles?: keyof typeof TILE_SRC;
  maxZoom?: number;
  // Place names drawn over a label-free basemap, so only the ones we choose
  // appear. Dropped if they'd fall outside the frame.
  labels?: { lat: number; lon: number; text: string; rotate?: number; kind?: 'place' | 'street' | 'water' }[];
  // Screens outside the framed view. Drawn as faint markers pinned to the
  // nearest edge with an arrow pointing toward their real position.
  outliers?: { lat: number; lon: number }[];
  // Show +/- buttons and drag-to-pan. The controls are tagged so html2canvas
  // leaves them out of PDF exports, which capture the current view.
  zoomControls?: boolean;
  children?: React.ReactNode;
}

// Free, key-less, CORS-enabled XYZ sources. (CARTO's free basemaps now serve
// an "API key required" placeholder tile, so they're not used.) Esri's Light
// Gray Canvas is a pale, label-free base; note its z/y/x ordering.
const TILE_SRC = {
  osm: { url: (z: number, x: number, y: number) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`, credit: '© OpenStreetMap contributors' },
  'esri-light': { recolor: true, url: (z: number, x: number, y: number) => `https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/${z}/${y}/${x}`, credit: 'Tiles © Esri' },
} as const;

export function StaticScreenMap({ screens, width, height, dot = 14, pad = 28, style, onFail, mapStyle = OSM_STYLE, renderDot, group, tiles, maxZoom = 18, outliers, labels, zoomControls, children }: Props) {
  const [failed, setFailed] = useState(false);
  // View adjustments relative to the auto-fit: zoom steps, and a pan offset in
  // world pixels at the fit zoom (so it stays put as the zoom changes).
  const [dz, setDz] = useState(0);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  const view = useMemo(() => {
    let pts = screens.filter(s => s.lat && s.lon).map(s => ({ lat: s.lat, lon: s.lon, n: 1 }));
    if (group) {
      const m = new Map<string, { lat: number; lon: number; n: number }>();
      pts.forEach(p => {
        const k = `${p.lat.toFixed(4)},${p.lon.toFixed(4)}`;
        const e = m.get(k);
        if (e) e.n += 1; else m.set(k, { ...p });
      });
      pts = Array.from(m.values());
    }
    if ((!API_KEY && !tiles) || !pts.length) return null;
    const fit = fitView(pts, width, height, pad, maxZoom);
    const baseZoom = fit.zoom;
    const baseC = project(fit.center.lat, fit.center.lon, baseZoom);
    const zoom = dz === 0 ? baseZoom : Math.min(16, Math.max(3, baseZoom + dz));
    const k = Math.pow(2, zoom - baseZoom);
    const c = { x: (baseC.x + off.x) * k, y: (baseC.y + off.y) * k };
    const center = unproject(c.x, c.y, zoom);
    const dots = pts.map(p => {
      const q = project(p.lat, p.lon, zoom);
      return { left: ((q.x - c.x + width / 2) / width) * 100, top: ((q.y - c.y + height / 2) / height) * 100, n: p.n };
    });
    const tileImgs: { key: string; src: string; left: number; top: number }[] = [];
    if (tiles) {
      const n = Math.pow(2, zoom);
      const x0 = Math.floor((c.x - width / 2) / TILE), x1 = Math.floor((c.x + width / 2) / TILE);
      const y0 = Math.floor((c.y - height / 2) / TILE), y1 = Math.floor((c.y + height / 2) / TILE);
      for (let tx = x0; tx <= x1; tx++) {
        for (let ty = y0; ty <= y1; ty++) {
          if (ty < 0 || ty >= n) continue;
          tileImgs.push({
            key: `${tx}/${ty}`,
            src: TILE_SRC[tiles].url(zoom, ((tx % n) + n) % n, ty),
            left: ((tx * TILE - (c.x - width / 2)) / width) * 100,
            top: ((ty * TILE - (c.y - height / 2)) / height) * 100,
          });
        }
      }
    }
    const url = `https://maps.googleapis.com/maps/api/staticmap?center=${center.lat.toFixed(6)},${center.lon.toFixed(6)}&zoom=${zoom}&size=${width}x${height}&scale=2&maptype=roadmap&${mapStyle.map(x => `style=${encodeURIComponent(x)}`).join('&')}&key=${API_KEY}`;
    // Pin each outlier to the frame edge along the line from the centre.
    const margin = 22;
    const edge = (outliers ?? []).filter(o => o.lat && o.lon).map(o => {
      const q = project(o.lat, o.lon, zoom);
      const dx = q.x - c.x, dy = q.y - c.y;
      const t = Math.min(1, (width / 2 - margin) / (Math.abs(dx) || 1), (height / 2 - margin) / (Math.abs(dy) || 1));
      return { left: ((width / 2 + dx * t) / width) * 100, top: ((height / 2 + dy * t) / height) * 100, angle: (Math.atan2(dy, dx) * 180) / Math.PI, clamped: t < 1 };
    });
    const labelPos = (labels ?? []).map(l => {
      const q = project(l.lat, l.lon, zoom);
      const left = ((q.x - c.x + width / 2) / width) * 100, top = ((q.y - c.y + height / 2) / height) * 100;
      return { ...l, left, top, inside: left > 8 && left < 92 && top > 5 && top < 95 };
    }).filter(l => l.inside);
    return { url, dots, tileImgs, edge, labelPos, zoom, k };
  }, [screens, width, height, pad, mapStyle, group, tiles, outliers, maxZoom, labels, dz, off]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (!zoomControls || (e.target as HTMLElement).closest('button')) return;
    drag.current = { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [zoomControls, off]);
  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current, box = boxRef.current;
    if (!d || !box || !view) return;
    const scale = width / box.getBoundingClientRect().width;
    setOff({ x: d.ox - ((e.clientX - d.x) * scale) / view.k, y: d.oy - ((e.clientY - d.y) * scale) / view.k });
  }, [view, width]);
  const endDrag = useCallback(() => { drag.current = null; }, []);

  if (!view || failed) return null;

  const ctrlBtn: React.CSSProperties = {
    width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: '#fff',
    color: '#2b3733', fontSize: 18, fontWeight: 600, lineHeight: 1, cursor: 'pointer', padding: 0, fontFamily: 'inherit',
  };

  return (
    <div
      ref={boxRef}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}
      style={{ position: 'relative', overflow: 'hidden', background: '#dbf0e4', ...(zoomControls ? { cursor: 'grab', touchAction: 'none', userSelect: 'none' } : {}), ...style }}
    >
      {tiles ? (
        <>
          {view.tileImgs.map(t => {
            const tileStyle: React.CSSProperties = { position: 'absolute', left: `${t.left}%`, top: `${t.top}%`, width: `${(TILE / width) * 100}%`, height: `${(TILE / height) * 100}%`, display: 'block' };
            return 'recolor' in TILE_SRC[tiles]
              ? <RecoloredTile key={t.key} src={t.src} style={tileStyle} />
              : <img key={t.key} src={t.src} crossOrigin="anonymous" alt="" draggable={false} style={tileStyle} />;
          })}
          <span style={{ position: 'absolute', right: 0, bottom: 0, padding: '1px 5px', fontSize: 9, background: 'rgba(255,255,255,0.7)', color: '#333' }}>{TILE_SRC[tiles].credit}</span>
        </>
      ) : (
      <img
        src={view.url}
        crossOrigin="anonymous"
        alt="Screen locations"
        style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
        onError={() => {
          console.warn('[StaticScreenMap] Static map image failed to load; check that REACT_APP_GOOGLE_MAPS_API_KEY has billing enabled and the Maps Static API enabled.');
          setFailed(true);
          onFail?.();
        }}
      />
      )}
      {view.labelPos.map(l => (
        <span key={l.text} style={{
          position: 'absolute', left: `${l.left}%`, top: `${l.top}%`, transform: `translate(-50%, -50%) rotate(${l.rotate ?? 0}deg)`,
          whiteSpace: 'nowrap', pointerEvents: 'none',
          ...(l.kind === 'street'
            ? { fontSize: 9.5, fontWeight: 500, color: '#3b4a44', textShadow: '0 0 2px rgba(219,240,228,0.9), 0 0 2px rgba(219,240,228,0.9)' }
            : l.kind === 'water'
              ? { fontSize: 9.5, fontWeight: 500, color: '#0c5f66', textShadow: '0 0 2px rgba(163,191,174,0.8)' }
              : { fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2b3733', textShadow: '0 0 3px rgba(219,240,228,0.95), 0 0 3px rgba(219,240,228,0.95)' }),
        }}>{l.text}</span>
      ))}
      {view.dots.map((d, i) => {
        const spec = renderDot ? renderDot(d.n) : { d: dot, style: { background: 'rgba(144,238,144,0.75)', border: '2px solid #1a8a1a' } } as DotSpec;
        return (
          <span key={i} style={{
            position: 'absolute', left: `${d.left}%`, top: `${d.top}%`,
            width: spec.d, height: spec.d, marginLeft: -spec.d / 2, marginTop: -spec.d / 2, boxSizing: 'border-box',
            borderRadius: '50%', ...spec.style,
          }}>{spec.label}</span>
        );
      })}
      {view.edge.map((e, i) => {
        const spec = renderDot ? renderDot(1) : { d: dot, style: { background: 'rgba(144,238,144,0.75)', border: '2px solid #1a8a1a' } } as DotSpec;
        return (
          <span key={`o${i}`} style={{ position: 'absolute', left: `${e.left}%`, top: `${e.top}%`, width: 0, height: 0 }}>
            <span style={{ position: 'absolute', width: spec.d, height: spec.d, marginLeft: -spec.d / 2, marginTop: -spec.d / 2, boxSizing: 'border-box', borderRadius: '50%', ...spec.style }} />
            {e.clamped && (
              <svg width="8" height="8" viewBox="0 0 8 8" style={{ position: 'absolute', left: -4, top: -4, overflow: 'visible', transform: `rotate(${e.angle}deg) translateX(${spec.d / 2 + 6}px)` }}>
                <path d="M0 0.5 L6 4 L0 7.5 Z" fill="#0f6b44" />
              </svg>
            )}
          </span>
        );
      })}
      {zoomControls && (
        <div data-html2canvas-ignore="true" style={{ position: 'absolute', top: 10, left: 10, display: 'flex', flexDirection: 'column', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
          <button type="button" aria-label="Zoom in" disabled={view.zoom >= 16} onClick={() => setDz(d => d + 1)} style={{ ...ctrlBtn, borderBottom: '1px solid #e2e8e5', opacity: view.zoom >= 16 ? 0.4 : 1 }}>+</button>
          <button type="button" aria-label="Zoom out" disabled={view.zoom <= 3} onClick={() => setDz(d => d - 1)} style={{ ...ctrlBtn, opacity: view.zoom <= 3 ? 0.4 : 1 }}>−</button>
        </div>
      )}
      {children}
    </div>
  );
}
